import React from "react";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Box,
  Typography,
  Stack,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface PageContainerProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: React.ReactNode;
  children: React.ReactNode;
  loading?: boolean;
}

// Standalone interface - guarantees zero dependency compilation errors
interface PageContainerTheme {
  palette: {
    mode: "dark" | "light";
    text: {
      primary: string;
      secondary: string;
    };
  };
  appRadius: {
    sm: string;
    md: string;
    lg: string;
  };
}

const PageContainer: React.FC<PageContainerProps> = ({
  title,
  subtitle,
  breadcrumbs = [],
  actions,
  children,
}) => {
  // Cast safely using unknown first (fully satisfies typescript and ESLint)
  const theme = useTheme() as unknown as PageContainerTheme;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        overflow: "hidden",
        bgcolor:
          theme.palette.mode === "dark" ? "background.default" : "#f4f6f8",
        position: "relative",
      }}
    >
      {/* --- HEADER SECTION --- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: 2,
          flexShrink: 0,
        }}
      >
        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mb: 1, "& .MuiTypography-root": { fontSize: "0.85rem" } }}
          >
            {breadcrumbs.map((crumb, index) => (
              <div key={index}>
                {crumb.to ? (
                  <Link
                    component={RouterLink}
                    to={crumb.to}
                    color="inherit"
                    underline="hover"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <Typography color="text.primary">{crumb.label}</Typography>
                )}
              </div>
            ))}
          </Breadcrumbs>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
          }}
        >
          {/* Title & Subtitle */}
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "text.primary" }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Page Actions */}
          {actions && <Box>{actions}</Box>}
        </Stack>
      </Box>

      {/* --- CONTENT SECTION --- */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          width: "100%",
          px: { xs: 2, md: 3 },
          pb: 3,
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
            margin: "4px 0",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.primary, 0.25),
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: alpha(theme.palette.text.primary, 0.45),
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default PageContainer;
